import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, ChevronDown, MessageSquare, ExternalLink, Play, XCircle, Pencil, Check, X, GripVertical, Plus, Trash2 } from 'lucide-react';
import { WORKFLOW_STEPS } from '@/utils/workflowSteps';
import { STATUS_CFG, PLAN_STATUS, getWfColor } from '@/utils/statusConfig';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import StatusSelect from './StatusSelect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HoverPhoto = ({ src, fallbackChar }) => {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <div className="shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
        <img src={src} alt=""
          className="w-11 h-11 rounded-full object-cover bg-slate-200 border border-slate-200 hover:border-slate-400 transition-all"
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white bg-[#0F172A]" style={{ display: 'none' }}>
          {fallbackChar}
        </div>
      </div>
      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={() => setModalOpen(false)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt="" className="w-72 h-72 rounded-xl object-cover shadow-2xl border-4 border-white" />
            <button onClick={() => setModalOpen(false)} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-600 hover:bg-red-50 hover:text-red-500 transition-colors text-lg font-bold">&times;</button>
          </div>
        </div>
      )}
    </>
  );
};

const TrackingRow = ({ emp, workflows, plans, isRTL, isAdmin, navigate, smartFilter, onStatusChange, onUpdateActualPaid, onExecuteStep, onCancelRemaining, onUpdateNote, onDeletePlan }) => {
  const [expanded, setExpanded] = useState(false);
  const hasPlans = plans && plans.length > 0;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: emp.employee_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };

  // Filter plans and steps based on smartFilter
  const filteredPlans = (() => {
    if (!hasPlans || smartFilter === 'all') return plans;
    if (smartFilter === 'executing') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'executing' || s.status === 'awaiting_approval')
    })).filter(p => p.steps.length > 0);
    if (smartFilter === 'awaiting_approval') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'awaiting_approval')
    })).filter(p => p.steps.length > 0);
    if (smartFilter === 'completed') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'completed')
    })).filter(p => p.steps.length > 0);
    return plans;
  })();

  // Filter workflows based on smartFilter
  const filteredWorkflows = (() => {
    if (smartFilter === 'all') return workflows;
    const statusMap = {
      executing: ['in_progress', 'pending', 'waiting_approval'],
      completed: ['completed'],
      awaiting_approval: ['waiting_approval'],
    };
    const targetStatuses = statusMap[smartFilter];
    if (!targetStatuses) return workflows;
    return workflows.map(wf => ({
      ...wf,
      steps: wf.steps.filter(s => targetStatuses.includes(s.status))
    })).filter(wf => wf.steps.length > 0);
  })();

  const hasWorkflows = filteredWorkflows && filteredWorkflows.length > 0;

  // Plan stats
  const planSteps = filteredPlans?.flatMap(p => p.steps || []) || [];
  const planTotal = planSteps.length;
  const planCompleted = planSteps.filter(s => s.status === 'completed').length;
  const planAwaiting = planSteps.filter(s => s.status === 'awaiting_approval').length;
  const planCounted = planCompleted + planAwaiting;
  const allPlanSteps = plans?.flatMap(p => p.steps || []) || [];
  const allTotal = allPlanSteps.length;
  const allCompleted = allPlanSteps.filter(s => s.status === 'completed').length;
  const allAwaiting = allPlanSteps.filter(s => s.status === 'awaiting_approval').length;
  const completedPct = allTotal > 0 ? Math.round((allCompleted / allTotal) * 100) : 0;
  const awaitingPct = allTotal > 0 ? Math.round((allAwaiting / allTotal) * 100) : 0;

  const primaryType = plans?.length > 0
    ? plans[plans.length - 1]?.steps?.[0]?.main_type || ''
    : filteredWorkflows[filteredWorkflows.length - 1]?.mainType || '';
  const rowColor = getWfColor(primaryType);

  // Show notes: plans with text show their note, only ONE empty "add note" button for the first plan
  const plansWithNotes = plans?.filter(p => (p.notes || '').trim()) || [];
  const firstPlanForAddNote = plans?.find(p => !(p.notes || '').trim());
  const planNotes = [
    ...plansWithNotes.map(p => ({ id: p.id, notes: p.notes.trim() })),
    ...(firstPlanForAddNote ? [{ id: firstPlanForAddNote.id, notes: '' }] : []),
  ];
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  return (
    <div ref={setNodeRef} style={style} {...attributes} className={`border-b-2 border-b-white last:border-b-0 relative rounded-lg mx-1 my-0.5 border ${rowColor.border} ${rowColor.bg} flex`} data-testid={`tracking-row-${emp.employee_id}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Employee Photo - Right in RTL, Left in LTR */}
      <div className="shrink-0">
        <img src={`${API}/employees/${emp.employee_id}/photo`} alt=""
          className="h-full object-cover bg-slate-100 rounded-s-lg"
          style={{ width: '60px', minHeight: '55px' }}
          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
        />
      </div>
      <div className="flex-1 min-w-0">
      <div className={`absolute top-0 bottom-0 w-1.5 ${rowColor.bar} rounded-full`} style={{ insetInlineStart: 0 }} />
      {/* Summary row */}
      <div className="flex items-center gap-2 px-4 py-2.5 hover:brightness-95 transition-all cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        {isAdmin && <div {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-white/50 rounded shrink-0" onClick={e => e.stopPropagation()} data-testid={`drag-handle-${emp.employee_id}`}>
          <GripVertical className="w-3.5 h-3.5 text-slate-400" />
        </div>}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />

        <div className="flex items-center gap-2 min-w-[160px] shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.employee_id}`); }}>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate leading-tight hover:text-[#D4AF37]">{emp.employee_name}</p>
            <p className="text-[10px] text-slate-400 truncate leading-tight">{emp.company_name}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-1 min-w-0 flex-wrap">
          {filteredWorkflows.map((wf, i) => {
            const wfc = getWfColor(wf.mainType);
            return (
              <span key={`wf-${i}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 border ${wfc.bg} ${wfc.text} ${wfc.border}`}>
                {isRTL ? wf.label.ar : wf.label.en}
              </span>
            );
          })}
        </div>

        <button className="p-1 hover:bg-slate-100 rounded shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.employee_id}`); }}>
          <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>

      {/* Visual Progress Steps */}
      {hasPlans && (
        <div className={`px-4 pb-2 ${isRTL ? 'pr-8' : 'pl-8'}`} data-testid={`progress-bar-${emp.employee_id}`}>
          <div className="flex items-center gap-1 flex-wrap" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            {planSteps.map((step, i) => {
              const isDone = step.status === 'completed';
              const isActive = step.status === 'executing';
              const isAwaiting = step.status === 'awaiting_approval';
              const isCancelled = step.status === 'cancelled';
              const isDoneUnpaid = step.status === 'done_unpaid';
              return (
                <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded ${
                  isDone ? 'bg-emerald-50 text-emerald-600 line-through' :
                  isAwaiting ? 'bg-blue-50 text-blue-700 font-semibold' :
                  isActive ? 'bg-amber-50 text-amber-700 font-semibold' :
                  isDoneUnpaid ? 'bg-purple-50 text-purple-600 font-semibold' :
                  isCancelled ? 'bg-red-50 text-red-500 line-through' :
                  'text-slate-400'
                }`}>
                  {isRTL ? step.label_ar : step.label_en}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {planNotes.map(pn => (
        <div key={pn.id} className={`flex items-start gap-1.5 px-4 pb-1.5 ${isRTL ? 'pr-8' : 'pl-8'}`} data-testid={`tracking-notes-${pn.id}`}>
          <MessageSquare className="w-3 h-3 text-amber-500 shrink-0 mt-1" />
          {editingNote === pn.id ? (
            <div className="flex items-center gap-1 flex-1">
              <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)} autoFocus
                className="flex-1 text-[11px] text-amber-700 font-medium bg-white border border-amber-300 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-amber-400"
                data-testid={`note-input-${pn.id}`}
                onKeyDown={e => { if (e.key === 'Enter') { onUpdateNote(pn.id, noteText); setEditingNote(null); } if (e.key === 'Escape') setEditingNote(null); }} />
              <button className="p-0.5 hover:bg-emerald-100 rounded" onClick={() => { onUpdateNote(pn.id, noteText); setEditingNote(null); }} data-testid={`note-save-${pn.id}`}>
                <Check className="w-3 h-3 text-emerald-600" />
              </button>
              <button className="p-0.5 hover:bg-red-100 rounded" onClick={() => setEditingNote(null)}>
                <X className="w-3 h-3 text-red-500" />
              </button>
            </div>
          ) : pn.notes ? (
            <div className="flex items-center gap-1 flex-1">
              <p className="text-[11px] text-amber-700 font-medium leading-relaxed cursor-pointer hover:underline flex items-center gap-1"
                onClick={(e) => { e.stopPropagation(); setEditingNote(pn.id); setNoteText(pn.notes); }}
                data-testid={`note-text-${pn.id}`}>
                {pn.notes}
                <Pencil className="w-2.5 h-2.5 text-amber-400 shrink-0" />
              </p>
              {isAdmin && onDeletePlan && (
                <button className="p-0.5 hover:bg-red-100 rounded shrink-0" onClick={(e) => { e.stopPropagation(); onDeletePlan(pn.id); }}
                  data-testid={`delete-note-${pn.id}`}>
                  <Trash2 className="w-2.5 h-2.5 text-red-400" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <button className="text-[10px] text-slate-400 hover:text-amber-600 cursor-pointer flex items-center gap-0.5"
                onClick={(e) => { e.stopPropagation(); setEditingNote(pn.id); setNoteText(''); }}
                data-testid={`add-note-${pn.id}`}>
                <Plus className="w-2.5 h-2.5" />
                {isRTL ? 'إضافة ملاحظة' : 'Add note'}
              </button>
              {isAdmin && onDeletePlan && (
                <button className="p-0.5 hover:bg-red-100 rounded shrink-0" onClick={(e) => { e.stopPropagation(); onDeletePlan(pn.id); }}
                  data-testid={`delete-plan-note-${pn.id}`}>
                  <Trash2 className="w-2.5 h-2.5 text-red-400" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-3">
          {(() => {
            const planStepByTxId = {};
            const planStepByTypeKey = {};
            const shownPlanStepIds = new Set();
            (plans || []).forEach(plan => {
              (plan.steps || []).forEach(step => {
                if (step.transaction_id) planStepByTxId[step.transaction_id] = { ...step, plan_id: plan.id };
                const key = `${step.main_type}::${step.sub_type}`;
                planStepByTypeKey[key] = { ...step, plan_id: plan.id };
              });
            });
            const findPlanStep = (txStep) => {
              if (!txStep.transaction) return null;
              if (planStepByTxId[txStep.transaction.id]) return planStepByTxId[txStep.transaction.id];
              const key = `${txStep.transaction.main_type}::${txStep.transaction.sub_type}`;
              return planStepByTypeKey[key] || null;
            };

            return (
              <>
                {hasWorkflows && filteredWorkflows.map((wf, wIdx) => {
                  const steps = wf.steps;
                  const hasUpcoming = wf.steps.some(s => s.status === 'upcoming');
                  const wfc = getWfColor(wf.mainType);
                  return (
                    <div key={`wf-${wIdx}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-5 rounded-full ${wfc.bar}`} />
                          <p className={`text-xs font-bold ${wfc.text}`}>{isRTL ? wf.label.ar : wf.label.en}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {isAdmin && (
                            <div className="flex items-center gap-4 text-[9px] font-semibold text-slate-400">
                              <span className="text-purple-400">{isRTL ? 'السعر' : 'Price'}</span>
                              <span className="text-amber-400">{isRTL ? 'المدفوع' : 'Paid'}</span>
                            </div>
                          )}
                          {isAdmin && hasUpcoming && (
                            <button onClick={() => onCancelRemaining(emp.employee_id, wf.mainType, wf.steps.filter(s => s.status === 'upcoming').map(s => s.sub_type))}
                              data-testid={`cancel-remaining-${wIdx}`}
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-red-500 border border-red-200 hover:bg-red-50 transition-colors">
                              <XCircle className="w-3 h-3" /> {isRTL ? 'إلغاء الباقي' : 'Cancel Remaining'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className={`rounded-lg border ${wfc.border} overflow-hidden`}>
                        {steps.map((step, idx) => {
                          const planStep = findPlanStep(step);
                          if (planStep) shownPlanStepIds.add(planStep.id);
                          const ps = planStep ? (PLAN_STATUS[planStep.status] || PLAN_STATUS.planned) : null;
                          return (
                            <div key={idx} className={`flex items-center gap-3 px-3 py-2 ${idx < steps.length - 1 ? 'border-b border-slate-50' : ''} ${step.status === 'in_progress' ? 'bg-amber-50/30' : ''} ${step.status === 'waiting_approval' ? 'bg-blue-50/30' : ''}`}>
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                                step.status === 'completed' ? 'bg-emerald-500' : step.status === 'in_progress' ? 'bg-amber-500' : step.status === 'waiting_approval' ? 'bg-blue-500' : step.status === 'cancelled' ? 'bg-red-400' : 'bg-slate-200'
                              }`}>
                                {step.status === 'completed' ? <CheckCircle2 className="w-3 h-3 text-white" /> :
                                 step.status === 'in_progress' || step.status === 'waiting_approval' ? <Clock className="w-3 h-3 text-white" /> :
                                 <span className="text-[8px] font-bold text-white/60">{idx + 1}</span>}
                              </div>
                              <span className={`text-xs flex-1 min-w-0 ${step.status === 'completed' ? 'text-slate-500 line-through' : step.status === 'upcoming' ? 'text-slate-400' : 'text-slate-700 font-medium'}`}>
                                {isRTL ? step.ar : step.en}
                              </span>
                              {step.transaction && (
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] text-purple-600 font-semibold" dir="ltr">{step.transaction.price?.toLocaleString() || 0}</span>
                                  {isAdmin && (
                                    <input type="text" inputMode="decimal" dir="ltr"
                                      key={`paid-${step.transaction.id}-${step.transaction.actual_paid_amount}`}
                                      data-testid={`actual-paid-${step.transaction.id}`}
                                      defaultValue={step.transaction.actual_paid_amount != null ? step.transaction.actual_paid_amount : ''}
                                      placeholder="0"
                                      className={`w-20 h-6 text-[11px] text-center border rounded-md focus:ring-1 outline-none ${step.transaction.actual_paid_amount == null ? 'border-red-300 bg-red-50/50 focus:border-red-400 focus:ring-red-400' : 'border-slate-200 bg-amber-50/50 focus:border-amber-400 focus:ring-amber-400'}`}
                                      onBlur={(e) => {
                                        const val = parseFloat(e.target.value);
                                        if (!isNaN(val) && val !== step.transaction.actual_paid_amount) {
                                          onUpdateActualPaid(step.transaction.id, val);
                                        }
                                      }}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  )}
                                </div>
                              )}
                              {isAdmin && step.transaction ? (
                                <StatusSelect currentStatus={step.transaction.status === 'pending' ? 'pending' : step.transaction.status} isRTL={isRTL}
                                  onSelect={(newStatus) => onStatusChange(step.transaction.id, newStatus)} />
                              ) : step.transaction ? (
                                <Badge className={`text-[10px] ${(STATUS_CFG[step.status === 'in_progress' ? 'pending' : step.status] || STATUS_CFG.pending).badge}`}>
                                  {isRTL ? (STATUS_CFG[step.status === 'in_progress' ? 'pending' : step.status] || STATUS_CFG.pending).ar : (STATUS_CFG[step.status === 'in_progress' ? 'pending' : step.status] || STATUS_CFG.pending).en}
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-slate-300">{isRTL ? 'لم يبدأ' : 'Not started'}</span>
                              )}
                              {ps && ps !== PLAN_STATUS.planned && <Badge className={`text-[9px] border shrink-0 ${ps.badge}`}>{isRTL ? ps.ar : ps.en}</Badge>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Unlinked plan steps */}
                {hasPlans && (() => {
                  const shownSubTypes = new Set();
                  filteredWorkflows.forEach(wf => {
                    wf.steps.forEach(s => {
                      if (s.transaction) shownSubTypes.add(`${s.transaction.main_type}::${s.transaction.sub_type}`);
                    });
                  });
                  const unlinked = [];
                  (filteredPlans || []).forEach(plan => {
                    (plan.steps || []).forEach(step => {
                      const key = `${step.main_type}::${step.sub_type}`;
                      if (!shownSubTypes.has(key) && !shownPlanStepIds.has(step.id)) {
                        unlinked.push({ ...step, plan_id: plan.id });
                        shownSubTypes.add(key);
                      }
                    });
                  });
                  if (!unlinked.length) return null;
                  const grouped = {};
                  unlinked.forEach(s => { const k = s.main_type || 'other'; if (!grouped[k]) grouped[k] = []; grouped[k].push(s); });
                  return Object.entries(grouped).map(([mainType, gSteps]) => {
                    const wfc = getWfColor(mainType);
                    return (
                      <div key={`unlinked-${mainType}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className={`w-1.5 h-5 rounded-full ${wfc.bar}`} />
                          <p className={`text-xs font-bold ${wfc.text}`}>{isRTL ? (WORKFLOW_STEPS[mainType]?.ar || mainType) : (WORKFLOW_STEPS[mainType]?.en || mainType)}</p>
                        </div>
                        <div className={`rounded-lg border ${wfc.border} overflow-hidden`}>
                          {gSteps.map((step, idx) => {
                            const ps = PLAN_STATUS[step.status] || PLAN_STATUS.planned;
                            return (
                              <div key={idx} className={`flex items-center gap-3 px-3 py-2 ${idx < gSteps.length - 1 ? `border-b ${wfc.border}` : ''} ${step.status === 'done_unpaid' ? 'bg-purple-50/30' : ''}`}>
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ps.bg}`}>
                                  {step.status === 'done_unpaid' ? <CheckCircle2 className="w-3 h-3 text-white" /> :
                                   <span className="text-[8px] font-bold text-slate-500">{idx + 1}</span>}
                                </div>
                                <span className="text-xs flex-1 min-w-0 text-slate-700 font-medium">{isRTL ? step.label_ar : step.label_en}</span>
                                <span className="text-[10px] font-semibold text-purple-600 shrink-0" dir="ltr">{step.price?.toLocaleString()}</span>
                                <Badge className={`text-[9px] border shrink-0 ${ps.badge}`}>{isRTL ? ps.ar : ps.en}</Badge>
                                {isAdmin && step.status === 'done_unpaid' && (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 shrink-0 border-blue-200 text-blue-600 hover:bg-blue-50"
                                    onClick={(e) => { e.stopPropagation(); onExecuteStep(step.plan_id, step.id); }}
                                    data-testid={`exec-plan-step-${idx}`}>
                                    <Play className="w-2.5 h-2.5" /> {isRTL ? 'تنفيذ' : 'Execute'}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </>
            );
          })()}
        </div>
      )}
      </div>
    </div>
  );
};

export default TrackingRow;
